import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { DataLayer } from "@sazka/utils/data-layer";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { AnalyticsMultiwagersBet } from "./analytics-bet";
import { AnalyticsCart } from "./analytics-cart";
import { AnalyticsError } from "./analytics-error";
import { AnalyticsOrder, AnalyticsMultibetOrder } from "./analytics-order";
import { AnalyticsPage } from "./analytics-page";
import { AnalyticsProduct } from "./analytics-product";

export class AnalyticsHelper {
    static cart: AnalyticsCart = new AnalyticsCart();
    static quickOrder: number = null;

    private static push(data: Record<any, any>): void {
        const dataClone = JSON.parse(JSON.stringify(data));
        DataLayer.push(dataClone);
    }

    public static pushCheckout(): void {
        this.push({ page: new AnalyticsPage("checkout", null, "checkout/widget.bet", { step: 1 }) });
    }

    public static pushPurchase(bettingSystem = null): void {
        const kind = AnalyticsHelper.cart.products[0].bettingMode;
        this.push({ page: new AnalyticsPage("purchase", null, kind, null, Analytics.lotteryCode(), bettingSystem) });
    }

    public static pushError(message: string, errorObject: AnalyticsError = null): void {
        if (!errorObject) {
            errorObject = new AnalyticsError("bet", message);
        }
        this.push({ page: new AnalyticsPage("error", errorObject) });
    }

    public static pushErrorCode(code: string = null): void {
        let message = "";
        let error: AnalyticsError = null;

        switch (code) {
            case "RESPONSIBLE_GAMING":
                message = "Responsible gaming limit reached.";
                break;

            case "INSUFFICIENT_FUNDS":
                message = "Not enough money.";
                break;

            case "DRAW_BREAK":
                message = "Draw break.";
                break;

            case "NOT_AUTORIZED":
                error = new AnalyticsError("other", "User is unauthorized.");
                break;

            default:
                message = code;
        }

        AnalyticsHelper.pushCart();
        AnalyticsHelper.pushOrder(0);
        AnalyticsHelper.pushError(message, error);
        AnalyticsHelper.measureEvent();
    }

    public static restoreCart(cart: AnalyticsCart) {
        this.cart = cart;
    }

    public static bet(
        gameName: string,
        value: number,
        bettingSystem: string = null,
        repetitionEndDate: string = null,
        tier: string = null,
        bettingMode: string = null
    ): void {
        const analyticsCodeName = Analytics.getLotteryCode(gameName);
        const eventProperty2 = this.quickOrder ? `quick${this.quickOrder}` : null;

        this.push({
            event: "action.bet.money",
            action: {
                bet: {
                    gameCode: analyticsCodeName,
                    value,
                    bettingSystem: bettingSystem,
                    repetitionEndDate,
                    tier,
                    bettingMode,
                    eventProperty2,
                },
            },
        });
    }

    public static firstBet(gameName: string, value: number): void {
        this.push({
            event: "action.firstBet.money",
            action: { firstBet: { gameCode: Analytics.getLotteryCode(gameName), value } },
        });
    }

    public static pushOrder(id: number): AnalyticsOrder {
        const order = new AnalyticsOrder(this.cart, id);
        this.push({ order: order });
        return order;
        //this.cart.empty();
    }

    public static pushCart(): void {
        this.push({ cart: this.cart });
    }

    public static pushProductsNull(): void {
        this.push({ cart: { products: null } });
        this.cart.empty();
    }

    public static pushLotteryBet(product: AnalyticsProduct): void {
        AnalyticsHelper.removeAllProducts();
        AnalyticsHelper.addProduct(product);
        AnalyticsHelper.pushCart();
        AnalyticsHelper.pushOrder(1);
        AnalyticsHelper.pushCheckout();
        AnalyticsHelper.measureEvent();
    }

    public static addProduct(product: AnalyticsProduct): void {
        this.push({ event: "action.add.products", action: { add: { products: [product] } } });
        this.cart.addProduct(product);
    }

    public static removeProduct(product: AnalyticsProduct): void {
        this.push({ event: "action.remove.products", action: { remove: { products: [product] } } });
        this.cart.empty();
    }

    public static removeAllProducts(): void {
        if (this.cart.products.length > 0) {
            this.push({ event: "action.remove.products", action: { remove: { products: this.cart.products } } });
            this.push({ cart: { products: null } });
            this.cart.empty();
        }
    }

    public static refund(id: string): void {
        this.push({ event: "action.refund.order", action: { refund: { order: { id: id } } } });
    }

    public static measureEvent(): void {
        this.push({ event: "page" });
    }

    public static pushClickButton(name: string, filling: string, price: number) {
        const sortedRules = ["manual", "randomColumn", "randomFull", "horoskop"];

        const sortedFilling = filling
            .split(",")
            .sort((stringA, stringB) => {
                return sortedRules.indexOf(stringA) - sortedRules.indexOf(stringB);
            })
            .join(",");

        this.push({
            event: "action.click.button",
            action: { click: { name: name, filling: sortedFilling, value: price } },
        });
    }

    public static getDrawDay(dayNumber: number): string {
        switch (dayNumber) {
            case 1:
                return "mo";
            case 2:
                return "tu";
            case 3:
                return "we";
            case 4:
                return "th";
            case 5:
                return "fr";
            case 6:
                return "sa";
            case 7:
                return "su";
        }
    }

    public static capitalizeFirstLetter(string: string): string {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    public static pushMultiwagersCheckout(
        elementsWithInfo: JQuery,
        cardPosition: number,
        multibetId: number,
        bettingMode = "multibets"
    ) {
        const card: AnalyticsCart = new AnalyticsCart();

        elementsWithInfo.each((_, element) => {
            const lotteryName = LotteryParser.parseLotteryName(element.getAttribute("data-lottery"));
            const product = AnalyticsProduct.MultiwagerGame(
                parseInt(element.getAttribute("data-price")),
                parseInt(element.getAttribute("data-boards")),
                parseInt(element.getAttribute("data-draws")),
                parseInt(element.getAttribute("data-duration")),
                lotteryName,
                element.dataset.addon == "True" ? LotteryParser.getAddon(lotteryName) : null,
                bettingMode
            );
            product.position = cardPosition;
            card.addProduct(product);
        });

        AnalyticsCart.pushEmptyCart();

        this.push({
            event: "page",
            page: new AnalyticsPage("checkout", null, bettingMode, { step: 1 }),
            order: {
                multibetID: multibetId,
            },
            cart: card,
        });
    }

    public static pushMultiwagerOrder(
        elementsWithInfo: JQuery,
        paymentData: any,
        multiwagerId: number,
        cardPosition: number,
        bettingMode = "multibets"
    ) {
        elementsWithInfo.each((index, element) => {
            const lotteryName = LotteryParser.parseLotteryName(element.getAttribute("data-lottery"));
            const orderId = paymentData.Payload.WagerIds[index];
            const cart = new AnalyticsCart();

            const product = AnalyticsProduct.MultiwagerGame(
                parseInt(element.getAttribute("data-price")),
                parseInt(element.getAttribute("data-boards")),
                parseInt(element.getAttribute("data-draws")),
                parseInt(element.getAttribute("data-duration")),
                lotteryName,
                element.dataset.addon == "True" ? LotteryParser.getAddon(lotteryName) : null,
                bettingMode
            );
            product.position = cardPosition;
            cart.addProduct(product);

            AnalyticsCart.pushEmptyCart();
            this.push({
                event: "page",
                page: new AnalyticsPage("purchase", null, bettingMode, null, Analytics.getLotteryCode(lotteryName)),
                cart: cart,
                order: new AnalyticsMultibetOrder(orderId, product.price, multiwagerId),
            });
        });
    }

    public static pushMultiwagerBetMoney(
        elementsWithInfo: JQuery,
        multibetName: string,
        multibetPosition: number,
        multibetID: number
    ) {
        let lotteryCodes = "";
        let lotteriesValue = 0;
        elementsWithInfo.each((index, element) => {
            const lotteryName = LotteryParser.parseLotteryName(element.getAttribute("data-lottery"));
            const value = parseInt(element.getAttribute("data-price"));
            const lotteryCode = Analytics.getLotteryCode(lotteryName);

            if (index !== 0) lotteryCodes += ",";
            lotteryCodes += `${lotteryCode}`;
            lotteriesValue += value;

            this.push({
                event: "action.bet.money",
                action: {
                    bet: new AnalyticsMultiwagersBet(lotteryCode, multibetName, multibetPosition, multibetID, value),
                },
            });
        });

        // DL2.20 Aggregated Bet
        this.push({
            event: "action.bet.money",
            action: {
                bet: new AnalyticsMultiwagersBet(
                    lotteryCodes,
                    multibetName,
                    multibetPosition,
                    multibetID,
                    lotteriesValue
                ),
            },
        });
    }
}
